/* eslint-disable quotes */
"use client"

import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Box,
  Button,
  Checkbox,
  FilledInput,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Modal,
} from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { LegacyRef, useEffect, useMemo, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import {
  CheckboxIcon,
  CheckedCheckboxIcon,
  CheckMark2Icon,
  Desktop,
  Mobile,
  NewText,
  SuccessFailJob,
} from "ui"
import { sharedColors as colors, sharedColors } from "ui/theme/colors"
import {
  jobFormClick,
  jobFormErrorClick,
  jobFormSuccessClick,
  postApply,
  schemaCommon,
} from "utils"
import * as yup from "yup"
import { ctaMessage } from "../Contact/ContactForm"
import FileUpload from "./FileUpload"
import UploadProgress from "./UploadProgress"

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const StyledForm = styled.form`
  width: 100%;
  min-height: 457px;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: fit-content;
    display: block;
  }
  input {
    font-size: 16px;
  }
  label {
    font-size: 16px;
  }
`
const StyledHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: boolean }>`
  margin-top: 4px;
  margin-bottom: 0;
  position: static;
  color: ${(props) => (props.status ? "#008015" : "#C92418")};
  font-size: 12px;
  height: 20px;
`
const StyledTextResponse = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const StyledInsideForm = styled.div<{ $withBackground: boolean }>`
  padding: ${(p) => (p.$withBackground ? "32px 16px" : "16px")};
  margin-bottom: 16px;
  height: 100%;
  .MuiFilledInput-root {
    height: 56px;
    input {
      padding-left: 8px;
      padding-right: 8px;
    }
  }
  .MuiInputLabel-root {
    transform: translate(8px, 16px) scale(1);
  }
  .MuiInputLabel-shrink {
    transform: translate(8px, 7px) scale(0.75);
  }
  background-color: ${(p) => (p.$withBackground ? "#F7F7F7" : "transparent")};
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: ${(p) => (p.$withBackground ? "32px" : "16px 32px")};
  }
`

const StyledReferralFormSection = styled.div`
  padding: 16px;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 16px 32px;
  }
`

const StyledAlign = styled.div`
  display: flex;
  justify-content: center;
`

const StyledTitle = styled.div`
  padding: 0 16px;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 0 32px;
  }
`
const StyledButton = styled(Button)`
  display: flex;
  height: 45px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  font-size: 18px;
  font-weight: 600;
  border-radius: 8px;
  width: 100%;
  max-width: 328px;
  padding: 8px 16px;
  margin: 0 24px;
  border: 1px solid ${(p) => p.theme.colors.buttonActiveBackgroundColor};
  color: ${(p) => p.theme.colors.buttonHoverTextColor};
  background: ${(p) => p.theme.colors.buttonActiveBackgroundColor};
  :hover {
    text-decoration: none;
    border: 1px solid ${(p) => p.theme.colors.buttonActiveBackgroundColor};
    background: ${(p) => p.theme.colors.buttonActiveBackgroundColor};
    color: ${(p) => p.theme.colors.buttonHoverTextColor};
  }
  &.Mui-disabled {
    color: ${(p) => p.theme.colors.gray};
    border: 1px solid ${(p) => p.theme.colors.lightGrey};
    background: ${(p) => p.theme.colors.lightGrey};
  }
`

const StyledCheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
`

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: boolean }>`
  padding: 1px 10px 0px 0;
  ${(props) =>
    !props.status
      ? `  rect {
    stroke: red;
  }`
      : ""}
`

const StyledModal = styled(Modal)`
  top: unset;
  bottom: 0;
  max-height: calc(100% - 80px);
  overflow: scroll;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: center;
  }
`
const env = process.env.ENV
const getMainSiteDomain = (): string => {
  if (env === "production") {
    return "https://www.ashtrom.co.il"
  } else if (env === "production_temp") {
    return "https://prod-app-b2b-main.azurewebsites.net"
  } else if (env === "staging") {
    return "https://testmain.ashtrom.co.il"
  } else {
    return "https://www.ashtrom.co.il"
  }
}

const schemaRegular = yup.object({
  firstName: schemaCommon.firstName,
  lastName: schemaCommon.lastName,
  email: schemaCommon.email,
  phone: schemaCommon.allPhoneNumber,
  fileName: yup
    .string()
    .required("נא לצרף קובץ קורות חיים")
    .test((value) => value?.trim().length > 0),
})

const schemaEmployee = yup.object({
  firstName: schemaCommon.firstName,
  lastName: schemaCommon.lastName,
  email: schemaCommon.emailNonRequired,
  phone: schemaCommon.allPhoneNumber,
  fileName: yup
    .string()
    .required("נא לצרף קובץ קורות חיים")
    .test((value) => value?.trim().length > 0),
  isApprovedTerms: yup
    .boolean()
    .oneOf([true], "עליך לאשר את התנאים כדי להגיש מועמדות")
    .required("עליך לאשר את התנאים כדי להגיש מועמדות"),
  referralName: schemaCommon.firstName,
  referralPhone: schemaCommon.allPhoneNumber,
  referralEmail: schemaCommon.email,
})

export type FormInput =
  | (yup.InferType<typeof schemaRegular> & { isReferral?: false })
  | (yup.InferType<typeof schemaEmployee> & { isReferral: true })

export type FileState = {
  fileName: string | null
  fileSize: number
  file: string | null
  fileError: string | null
}
const FormComponent = ({
  orderId,
  jobType,
  messages,
  isReferral,
}: {
  orderId: number
  jobType?: any
  messages?: any
  setModalOpen?: any
  isReferral: boolean
}) => {
  const router = useRouter()
  const [progress, setProgress] = useState<boolean>(false)
  const [isSubmittedForm, setIsSubmittedForm] = useState(false)
  const [submitState, setSubmitState] = useState<"success" | "fail">("fail")
  const [modalOpen, setModalOpen] = useState(false)
  const t = useTranslations("ProjectsPage")
  const [fileState, setFileState] = useState<FileState>({
    fileName: null,
    fileSize: 0,
    file: null,
    fileError: null,
  })
  const reCaptchaRef = useRef<ReCAPTCHA>()

  const scrollToForm = () => {
    const target = document.getElementById("target-section")
    const top = (target?.offsetTop || 0) - 200
    window.scrollTo({ top, behavior: "smooth" })
  }

  const handleSuccess = () => {
    router.push("/career")
  }

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitted, isValid },
    reset,
    trigger,
  } = useForm<FormInput>({
    resolver: yupResolver(isReferral ? schemaEmployee : schemaRegular),
  })
  const { mutate } = useMutation({
    mutationKey: ["postForm"],
    mutationFn: postApply,
    onSuccess: () => {
      jobFormSuccessClick("שליחה", jobType, "טופס בעמוד משרה", false)
      setSubmitState("success")
      setIsSubmittedForm(true)
      setModalOpen(true)
      reset()
    },
    onError: (error) => {
      jobFormErrorClick("שליחה", jobType, "טופס בעמוד משרה", error.message)
      setSubmitState("fail")
      setIsSubmittedForm(true)
      setModalOpen(true)
      reset()
      scrollToForm()
    },
  })

  useEffect(() => {
    if (messages && messages.checkbox) {
      messages.checkbox = messages.checkbox
        .replace(
          "privacy policy",
          `<a href="${getMainSiteDomain()}/content/privacy-policy" style="color: black">privacy policy</a>`,
        )
        .replace(
          "terms of use",
          `<a href="${getMainSiteDomain()}/content/terms-of-use" style="color: black">terms of use</a>`,
        )

      messages.checkbox = messages.checkbox
        .replace(
          "מדיניות הפרטיות",
          `<a href="${getMainSiteDomain()}/content/privacy-policy" style="color: black">מדיניות הפרטיות</a>`,
        )
        .replace(
          "תנאי השימוש",
          `<a href="${getMainSiteDomain()}/content/terms-of-use" style="color: black">תנאי השימוש</a>`,
        )
    }
  }, [messages])

  useEffect(() => {
    reset({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      referralEmail: "",
      referralName: "",
      referralPhone: "",
      isApprovedTerms: false,
      fileName: "",
    })
    setFileState({
      fileName: null,
      fileSize: 0,
      file: null,
      fileError: null,
    })
  }, [isReferral, reset])

  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    if (!fileState.file || fileState.fileError) {
      return
    }

    reCaptchaRef?.current?.reset()
    const recaptchaResponse = (await reCaptchaRef?.current?.executeAsync()) as string
    setModalOpen(true)
    mutate({
      cvFileBase64: fileState.file,
      email: data.email || "",
      fileNameWithExtension: fileState.fileName!,
      firstName: data.firstName,
      isApprovedTerms: true,
      lastName: data.lastName,
      orderId: orderId,
      phone: data.phone,
      gRecaptchaResponse: recaptchaResponse,
      isReferral: isReferral,
      referralEmail: (data as yup.InferType<typeof schemaEmployee>)?.referralEmail,
      referralName: (data as yup.InferType<typeof schemaEmployee>)?.referralName,
      referralPhone: (data as yup.InferType<typeof schemaEmployee>)?.referralPhone,
    })
    setProgress(true)
  }

  const mandatoryText = useMemo(() => {
    if (isReferral) {
      return messages?.mandatoryReferral
        ? messages?.mandatoryReferral
        : "יש למלא את הפרטים שלך כדי שנדע למי לפרגן"
    }
    return messages?.mandatory
      ? messages?.mandatory
      : "פעולה קצרה, שיכולה להוביל להזדמנות גדולה. בואו נכיר"
  }, [isReferral, messages?.mandatory, messages?.mandatoryReferral])

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)} id="target-section">
      {progress ? (
        <StyledModal open={modalOpen} onClose={() => setModalOpen(false)}>
          <UploadProgress
            filename={fileState.fileName}
            setProgress={setProgress}
            filesize={fileState.fileSize}
            setFileState={setFileState}
          />
        </StyledModal>
      ) : null}
      {!progress && isSubmittedForm ? (
        <StyledModal open={modalOpen} onClose={() => setModalOpen(false)}>
          <SuccessFailJob
            isReferral={isReferral}
            state={submitState}
            setIsSubmitted={setIsSubmittedForm}
            handleSuccess={handleSuccess}
          />
        </StyledModal>
      ) : null}
      {!progress && !isSubmittedForm ? (
        <>
          {!isReferral && (
            <StyledTitle>
              {isReferral && (
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  color={colors.black}
                  mb={2}
                >
                  פרטי הממליץ/ה
                </NewText>
              )}
              <NewText
                component="p"
                desktopFontSize={18}
                desktopLineHeight="26px"
                color={colors.black}
              >
                {mandatoryText}
              </NewText>
            </StyledTitle>
          )}
          <StyledInsideForm $withBackground={isReferral}>
            {isReferral && (
              <>
                <Box
                  display={"flex"}
                  gap={"16px"}
                  alignItems={"flex-end"}
                  flexWrap={"wrap"}
                  sx={{
                    marginBottom: "8px",
                    "@media (max-width:600px)": {
                      marginBottom: "16px",
                    },
                  }}
                >
                  <Mobile>
                    <Image src={"/images/CareerLogo.png"} alt="logo" width={61} height={45} />
                  </Mobile>
                  <Desktop>
                    <Image src={"/images/CareerLogo.png"} alt="logo" width={90} height={66} />
                  </Desktop>
                  <NewText
                    component="p"
                    desktopFontSize={24}
                    desktopLineHeight="35px"
                    mobileFontSize={18}
                    mobileLineHeight={"26px"}
                    color={colors.black}
                  >
                    מה יותר טוב מלעבוד{" "}
                    <NewText
                      component={"span"}
                      desktopFontSize={24}
                      desktopLineHeight="35px"
                      mobileFontSize={18}
                      mobileLineHeight={"26px"}
                      desktopFontWeight={"700"}
                      color={colors.black}
                    >
                      בקבוצת אשטרום?
                    </NewText>
                  </NewText>
                </Box>
                <Box
                  sx={{
                    marginBottom: "24px",
                    "@media (max-width:600px)": {
                      marginBottom: "32px",
                    },
                  }}
                >
                  <NewText
                    component="p"
                    desktopFontSize={20}
                    desktopLineHeight="29px"
                    mobileFontSize={16}
                    mobileLineHeight={"23px"}
                    color={colors.black}
                  >
                    לצרף את החברים שלך ולהנות ביחד מכל הטוב הזה! זה המקום להמליץ על חברים – אנחנו
                    בונים על א.נשים מעולים, בדיוק כמוך.
                  </NewText>
                </Box>
              </>
            )}
            {isReferral && (
              <NewText
                component="p"
                desktopFontSize={18}
                desktopLineHeight="26px"
                color={colors.black}
                mb={2}
              >
                פרטי החבר/ה
              </NewText>
            )}
            <Grid container spacing={3} rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <FormControl variant="filled" fullWidth>
                      <InputLabel>
                        {isReferral
                          ? messages?.candidateFirstname
                          : messages?.firstname
                          ? messages?.firstname
                          : "שם פרטי*"}
                      </InputLabel>
                      <FilledInput {...field} error={!!errors.firstName} fullWidth />
                      <StyledHelperText status={!errors.firstName}>
                        <StyledTextResponse>
                          {isSubmitted ? (
                            <>
                              <CheckMark2Icon state={!errors.firstName} />
                              <span>
                                {errors.firstName ? errors.firstName.message : t("completed")}
                              </span>
                            </>
                          ) : null}
                        </StyledTextResponse>
                      </StyledHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <FormControl variant="filled" fullWidth>
                      <InputLabel>
                        {isReferral
                          ? messages?.candidateLastname
                          : messages?.lastname
                          ? messages?.lastname
                          : "שם משפחה*"}
                      </InputLabel>
                      <FilledInput {...field} error={!!errors.lastName} fullWidth />
                      <StyledHelperText status={!errors.lastName}>
                        {isSubmitted ? (
                          <>
                            <StyledTextResponse>
                              <CheckMark2Icon state={!errors.lastName} />
                              <span>
                                {errors.lastName ? errors.lastName.message : t("completed")}
                              </span>
                            </StyledTextResponse>
                          </>
                        ) : null}
                      </StyledHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="phone"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <FormControl variant="filled" fullWidth>
                      <InputLabel>
                        {isReferral
                          ? messages?.candidatePhone
                          : messages?.phone
                          ? `${messages?.phone}`
                          : `טלפון*`}
                      </InputLabel>
                      <FilledInput {...field} error={!!errors.phone} fullWidth />
                      <StyledHelperText status={!errors.phone}>
                        {isSubmitted ? (
                          <>
                            <StyledTextResponse>
                              <CheckMark2Icon state={!errors.phone} />
                              <span>{errors.phone ? errors.phone.message : t("completed")}</span>
                            </StyledTextResponse>
                          </>
                        ) : null}
                      </StyledHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <FormControl variant="filled" fullWidth>
                      <InputLabel>
                        {isReferral
                          ? messages?.candidateEmail
                          : messages?.email
                          ? messages?.email
                          : "כתובת דוא״ל*"}
                      </InputLabel>
                      <FilledInput {...field} error={!!errors.email} fullWidth />
                      <StyledHelperText status={!errors.email}>
                        {isSubmitted ? (
                          <>
                            <StyledTextResponse>
                              <CheckMark2Icon state={!errors.email} />
                              <span>{errors.email ? errors.email.message : t("completed")}</span>
                            </StyledTextResponse>
                          </>
                        ) : null}
                      </StyledHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FileUpload
                  setValue={setValue}
                  formError={errors.fileName}
                  fileState={fileState}
                  setFileState={setFileState}
                  isSubmitted={isSubmitted}
                  setProgress={setProgress}
                  trigger={trigger}
                  attach={isReferral ? messages?.candidateAttach : messages?.attach}
                  backgroundColor="#FFF"
                />
              </Grid>
              {isReferral && (
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    "@media (max-width:600px)": {
                      paddingTop: "52px !important",
                    },
                  }}
                >
                  <StyledCheckboxContainer>
                    <StyledCheckbox
                      icon={<CheckboxIcon />}
                      checkedIcon={<CheckedCheckboxIcon color={sharedColors.corpBlue} />}
                      sx={{
                        ":hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                      status={true}
                      value={getValues("isApprovedTerms")}
                      onChange={(e) => {
                        const checked = e.target?.checked
                        setValue("isApprovedTerms", checked, { shouldValidate: true })
                      }}
                    />

                    <NewText
                      desktopFontSize={14}
                      desktopFontWeight="400"
                      desktopLineHeight="20px"
                      mobileFontSize={12}
                      mobileFontWeight="400"
                      mobileLineHeight={"17px"}
                      component="p"
                      color={"black"}
                      maxWidth={"750px"}
                      dangerouslySetInnerHTML={{ __html: messages.checkbox }}
                    />
                  </StyledCheckboxContainer>
                  <StyledHelperText status={!(errors as any).isApprovedTerms}>
                    {isSubmitted ? (
                      <>
                        <StyledTextResponse>
                          <CheckMark2Icon state={!(errors as any).isApprovedTerms} />
                          <span>
                            {(errors as any).isApprovedTerms
                              ? (errors as any).isApprovedTerms.message
                              : t("completed")}
                          </span>
                        </StyledTextResponse>
                      </>
                    ) : null}
                  </StyledHelperText>
                </Grid>
              )}
            </Grid>
          </StyledInsideForm>
          {isReferral && (
            <StyledTitle>
              {isReferral && (
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  color={colors.black}
                  mb={2}
                >
                  פרטי הממליץ/ה
                </NewText>
              )}
              <NewText
                component="p"
                desktopFontSize={18}
                desktopLineHeight="26px"
                color={colors.black}
              >
                {mandatoryText}
              </NewText>
            </StyledTitle>
          )}
          {isReferral && (
            <StyledReferralFormSection>
              <Grid container spacing={3} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="referralName"
                    control={control}
                    render={({ field }) => (
                      <FormControl variant="filled" fullWidth>
                        <InputLabel>{messages?.referralName}</InputLabel>
                        <FilledInput {...field} error={!!(errors as any).referralName} fullWidth />
                        <StyledHelperText status={!(errors as any).referralName}>
                          <StyledTextResponse>
                            {isSubmitted ? (
                              <>
                                <CheckMark2Icon state={!(errors as any).referralName} />
                                <span>
                                  {(errors as any).referralName
                                    ? (errors as any).referralName.message
                                    : t("completed")}
                                </span>
                              </>
                            ) : null}
                          </StyledTextResponse>
                        </StyledHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="referralEmail"
                    control={control}
                    render={({ field }) => (
                      <FormControl variant="filled" fullWidth>
                        <InputLabel>{messages?.referralEmail || "כתובת דוא״ל"}</InputLabel>
                        <FilledInput {...field} error={!!(errors as any).referralEmail} fullWidth />
                        <StyledHelperText status={!(errors as any).referralEmail}>
                          {isSubmitted ? (
                            <>
                              <StyledTextResponse>
                                <CheckMark2Icon state={!(errors as any).referralEmail} />
                                <span>
                                  {(errors as any).referralEmail
                                    ? (errors as any).referralEmail.message
                                    : t("completed")}
                                </span>
                              </StyledTextResponse>
                            </>
                          ) : null}
                        </StyledHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="referralPhone"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <FormControl variant="filled" fullWidth>
                        <InputLabel>{messages?.referralPhone || `טלפון*`}</InputLabel>
                        <FilledInput {...field} error={!!(errors as any).referralPhone} fullWidth />
                        <StyledHelperText status={!(errors as any).referralPhone}>
                          {isSubmitted ? (
                            <>
                              <StyledTextResponse>
                                <CheckMark2Icon state={!(errors as any).referralPhone} />
                                <span>
                                  {(errors as any).referralPhone
                                    ? (errors as any).referralPhone.message
                                    : t("completed")}
                                </span>
                              </StyledTextResponse>
                            </>
                          ) : null}
                        </StyledHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </StyledReferralFormSection>
          )}
          <Box
            sx={{
              padding: "32px",
              "@media (max-width:600px)": {
                padding: "30px 24px",
              },
            }}
          >
            {isReferral && (
              <NewText
                desktopFontSize={14}
                desktopFontWeight="400"
                desktopLineHeight="20px"
                mobileFontWeight="400"
                component="p"
                color={"black"}
                maxWidth={"750px"}
              >
                *אין להעביר קורות חיים או פרטים אודות חבר/ה ללא ידיעתם
              </NewText>
            )}
            <NewText
              desktopFontSize={14}
              desktopFontWeight="400"
              desktopLineHeight="20px"
              mobileFontWeight="400"
              component="p"
              color={"black"}
              maxWidth={"750px"}
              dangerouslySetInnerHTML={{
                __html: `${isReferral ? "*" : ""}${ctaMessage(
                  t(isReferral ? "ctaMessageForRecommendAFriend" : "ctaMessage") || "",
                )}`,
              }}
            />
          </Box>
          <StyledAlign>
            <StyledButton
              type="submit"
              onClick={() => {
                jobFormClick("שליחה", jobType, "טופס בעמוד משרה")

                Object.keys(errors).length > 0 ||
                  (!isValid &&
                    jobFormErrorClick(
                      "שליחה",
                      jobType,
                      "טופס בעמוד משרה",
                      errors?.email?.message ||
                        errors?.fileName?.message ||
                        errors?.firstName?.message ||
                        errors?.lastName?.message ||
                        errors?.phone?.message ||
                        "Fields are mandatory",
                    ))
              }}
              disabled={isSubmitted && (Object.keys(errors).length > 0 || !isValid)}
            >
              {messages?.send ? messages?.send : "שליחה"}
            </StyledButton>
          </StyledAlign>
        </>
      ) : null}
      {RECAPTCHA_KEY && (
        <ReCAPTCHA
          ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
          sitekey={RECAPTCHA_KEY}
          size="invisible"
          hl="iw"
          tabIndex={-1}
        />
      )}
    </StyledForm>
  )
}

export default FormComponent
